import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star } from "lucide-react";
import { IMG, LINKS, scrollToSection } from "../config";
import { useLang } from "../i18n";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t, isAr, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative h-screen min-h-[680px] overflow-hidden bg-forest" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={IMG.hero} alt="Sipsation Coffee signature latte" className="w-full h-[125%] object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-b from-forest/80 via-forest/55 to-forest" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest/85 via-forest/20 to-transparent" />
      </motion.div>

      <motion.div key={lang} style={{ opacity: fade }} className="relative z-10 h-full px-6 md:px-12 max-w-[1400px] mx-auto flex flex-col justify-center">
        <MaskedLine delay={0.35}>
          <span className="text-[11px] uppercase tracking-[0.4em] text-gold font-body">{t.hero.kicker}</span>
        </MaskedLine>
        <h1
          className={`mt-6 font-display text-paper tracking-tighter ${
            isAr
              ? "font-normal text-4xl md:text-6xl lg:text-[5.5vw] leading-[1.45]"
              : "font-light text-5xl md:text-7xl lg:text-[7vw] leading-[0.95]"
          }`}
          data-testid="hero-headline"
        >
          <MaskedLine delay={0.5}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.65}>
            <span className="italic text-gold">{t.hero.line2}</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.85} className="mt-6">
          <p
            className={`${t.hero.taglineArabicFont ? "font-arabic text-2xl md:text-4xl" : "font-display italic text-xl md:text-3xl"} text-gold/90`}
            data-testid="hero-arabic-tagline"
          >
            {t.hero.tagline}
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={scrollToSection("#menu")}
            data-testid="hero-menu-button"
            className="bg-gold text-forest font-semibold text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-none hover:bg-golddeep hover:text-paper transition-colors duration-300"
          >
            {t.hero.explore}
          </button>
          <a
            href={LINKS.talabat}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-order-button"
            className="border border-paper/30 text-paper text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-none hover:border-gold hover:text-gold transition-colors duration-300"
          >
            {t.hero.order}
          </a>
          <span className="flex items-center gap-2 text-paper/70 text-sm mx-2" data-testid="hero-rating">
            <Star size={14} className="fill-gold text-gold" />
            {t.hero.rating}
          </span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-12 z-10 flex items-center gap-4"
        data-testid="hero-scroll-indicator"
      >
        <div className="w-px h-14 bg-paper/20 relative overflow-hidden">
          <motion.div
            className="absolute top-0 left-0 w-px h-6 bg-gold"
            animate={{ y: [0, 56] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        <span className="text-[10px] uppercase tracking-[0.3em] text-paper/50">{t.hero.scroll}</span>
      </motion.div>
    </section>
  );
};
